import HeaderBrand from "@/shared/layout/Header/HeaderBrand";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import React, { useState } from "react";

import { getPermissionModules } from "@/D2C/features/auth/utils/permissions";
import { SecureNavBarItems } from "@/shared/config/header.config";
import { getThemeComponentGroup } from "@/shared/lib/themeComponentClasses";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

export default function AuthSidebar(): React.JSX.Element {
  const {
    auth: { user },
  } = usePosterReducers();
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const permissionModules = getPermissionModules(user?.user);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const sidebarClasses = getThemeComponentGroup("sidebar");
  const sidebarBgClass = sidebarClasses?.bgColor || "bg-theme-accent";
  const sidebarTextClass = sidebarClasses?.textColor || "text-theme-text";
  const activeItemBgClass = sidebarClasses?.activeItemBgColor || "bg-theme-text/10";
  const activeItemTextClass = sidebarClasses?.activeItemTextColor || sidebarTextClass;
  const inactiveItemTextClass = sidebarClasses?.inactiveItemTextColor || "text-theme-text/60";
  const toggleButtonBgClass =
    sidebarClasses?.toggleButtonBgColor || "bg-theme-text/10 hover:bg-theme-text/20";
  const toggleIconClass = sidebarClasses?.toggleIconColor || sidebarTextClass;

  const filteredNavItems = SecureNavBarItems.filter((item) => {
    if (!item.module) return false;

    return permissionModules.some((module) => module.module_name === item.module);
  });

  const getActiveTab = () => {
    const currentPath = pathname.split("?")[0];

    const activeItem = filteredNavItems.find(
      (item) => currentPath === item.path || currentPath.startsWith(`${item.path}/`)
    );

    return activeItem?.id ?? "";
  };

  const callOnTabChange = (item) => {
    navigate(item.path);

    // Implement any additional logic needed when a tab is changed
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  //console.log("Permission Modules:", permissionModules, "Filtered Nav Items:", filteredNavItems);
  return (
    <aside
      className={`
         inset-y-0 left-0 z-40 transform transition-all duration-300 ease-in-out overflow-y-auto thin-scrollbar
        md:translate-x-0 border-r border-theme-border/50 flex flex-col
        ${sidebarBgClass} ${sidebarTextClass} ${sidebarClasses?.root || ""}
        ${isCollapsed ? "w-16" : "w-64"}
      `}
    >
      {/* Branch Header */}
      <div className={`mb-5 mt-5 ${isCollapsed ? "mx-2 flex justify-center" : "mx-7"}`}>
        {!isCollapsed && <HeaderBrand variant="sidebar" />}
        {isCollapsed && (
          <div className="flex justify-center transform scale-50 origin-center">
            <HeaderBrand variant="sidebar" />
          </div>
        )}
      </div>

      <nav className="flex-1 pt-0">
        {filteredNavItems?.map((item) => (
          <button
            key={item.id}
            id={`sidebar-item-${item.id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => callOnTabChange(item)}
            className={`w-full flex items-center gap-3 py-4 transition-all 
              ${isCollapsed ? "justify-center px-2" : "px-6"}
              ${
                getActiveTab() === item.id
                  ? `${activeItemBgClass} border-r-4 border-current font-bold ${activeItemTextClass} ${sidebarClasses?.activeItem || ""}`
                  : `${inactiveItemTextClass} hover:bg-theme-text/5 ${sidebarClasses?.inactiveItem || ""}`
              }
              `}
          >
            <item.icon size={20} />
            {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
          </button>
        ))}
      </nav>

      {/* Collapsible Button */}
      <div className={`py-4 ${isCollapsed ? "flex justify-center" : "flex justify-end pr-4"}`}>
        <button
          id="admin-sidebar-toggle"
          onClick={toggleSidebar}
          className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${toggleButtonBgClass} ${sidebarClasses?.toggleButton || ""}`}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronRight size={20} className={toggleIconClass} />
          ) : (
            <ChevronLeft size={20} className={toggleIconClass} />
          )}
        </button>
      </div>
    </aside>
  );
}
